'use client';

import type { Route } from 'next';
import dynamic from 'next/dynamic';
import Link from 'next/link';
import type { ReactNode } from 'react';
import { EnvironmentIndicator } from '@/components/common/EnvironmentIndicator';
import { StaticPageLayoutAuthHydrator } from '@/components/common/StaticPageLayoutAuthHydrator';
import { ThemeAwareLogo } from '@/components/common/ThemeAwareLogo';
import { buttonVariants } from '@/components/ui/button';

type FooterLink = { href: Route; label: string; external?: false } | { href: string; label: string; external: true };

const FOOTER_LINKS: FooterLink[] = [
  { href: TERMS_ROUTE, label: 'Terms' },
  { href: PRIVACY_ROUTE, label: 'Privacy' },
  { href: PRICING_ROUTE, label: 'Pricing' },
  { href: 'https://status.sweetistics.com', label: 'Status', external: true },
  { href: 'https://roadmap.sweetistics.com/docs', label: 'Docs', external: true },
  { href: CONTACT_ROUTE, label: 'Contact' },
];

import {
  AUTH_SIGNIN_ROUTE,
  CONTACT_ROUTE,
  FEATURES_ROUTE,
  PRICING_ROUTE,
  PRIVACY_ROUTE,
  ROOT_ROUTE,
  TERMS_ROUTE,
  TIMELINE_ROUTE,
} from '@/lib/routes/app';
import { cn } from '@/lib/utils';

const ThemeSlider = dynamic(
  async () => import('@/components/common/ThemeSlider').then((module) => module.ThemeSlider),
  {
    ssr: false,
    loading: () => (
      <div
        aria-hidden="true"
        className="h-8 w-32 rounded-full border border-border/60 bg-card/60 dark:border-border/40"
      />
    ),
  }
);

interface StaticPageLayoutProperties {
  readonly children: ReactNode;
}

const NAV_LINKS = [
  { href: FEATURES_ROUTE, label: 'Features' },
  { href: PRICING_ROUTE, label: 'Pricing' },
  { href: CONTACT_ROUTE, label: 'Contact' },
] as const;

export function StaticPageLayout({ children }: StaticPageLayoutProperties) {
  const defaultCtaHref: Route = AUTH_SIGNIN_ROUTE;
  const defaultCtaLabel = 'Login';
  const defaultCtaClass = cn(buttonVariants({ variant: 'outline', size: 'sm' }), 'font-semibold');
  const authenticatedCtaClass = cn(buttonVariants({ variant: 'default', size: 'sm' }), 'font-semibold');

  return (
    <div className="flex min-h-screen flex-col">
      <MarketingHeaderShell
        defaultCtaHref={defaultCtaHref}
        defaultCtaLabel={defaultCtaLabel}
        defaultCtaClass={defaultCtaClass}
        authenticatedCtaClass={authenticatedCtaClass}
      />
      <main className="flex-1">{children}</main>
      <MarketingFooterShell />
    </div>
  );
}

interface MarketingHeaderShellProperties {
  readonly authenticatedCtaClass: string;
  readonly defaultCtaClass: string;
  readonly defaultCtaHref: Route;
  readonly defaultCtaLabel: string;
}

function MarketingHeaderShell({
  defaultCtaHref,
  defaultCtaLabel,
  defaultCtaClass,
  authenticatedCtaClass,
}: MarketingHeaderShellProperties) {
  return (
    <header className="sticky top-0 z-layer-chrome w-full border-b border-border/60 bg-background/90 backdrop-blur-md supports-[backdrop-filter]:bg-card/80 shadow-sm">
      <div className="container mx-auto flex h-16 max-w-6xl items-center justify-between px-4">
        <Link href="/" className="flex items-center gap-2" aria-label="Sweetistics home">
          <ThemeAwareLogo alt="Sweetistics" width={32} height={32} className="h-8 w-8 object-contain" />
          <span className="text-lg font-semibold tracking-tight">Sweetistics</span>
        </Link>

        <nav className="flex flex-wrap items-center gap-6 max-sm:hidden" aria-label="Primary navigation">
          {NAV_LINKS.map(({ href, label }) => (
            <Link
              key={href}
              href={href}
              className="rounded-md px-2 py-1 text-sm font-semibold text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/35 focus-visible:ring-offset-2 focus-visible:ring-offset-background"
            >
              {label}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-2" data-marketing-auth-shell>
          <EnvironmentIndicator />
          <Link
            href={defaultCtaHref}
            className={defaultCtaClass}
            data-marketing-auth-cta
            data-login-class={defaultCtaClass}
            data-auth-class={authenticatedCtaClass}
            data-auth-href={TIMELINE_ROUTE}
            data-auth-label="Open Timeline"
            data-login-label={defaultCtaLabel}
          >
            {defaultCtaLabel}
          </Link>
          <details className="relative sm:hidden">
            <summary
              aria-label="Open navigation menu"
              className="flex h-9 w-9 cursor-pointer list-none items-center justify-center rounded-md border border-border/60 bg-card/80 transition-colors hover:bg-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/35 focus-visible:ring-offset-2 focus-visible:ring-offset-background"
            >
              <span aria-hidden className="inline-flex size-5 items-center justify-center">
                <MobileMenuIcon />
              </span>
            </summary>
            <div className="absolute right-0 mt-3 w-44 space-y-2 rounded-lg border border-border/60 bg-background/90 p-3 shadow-lg backdrop-blur-md supports-[backdrop-filter]:bg-card/80 dark:supports-[backdrop-filter]:bg-background/60">
              {NAV_LINKS.map(({ href, label }) => (
                <Link
                  key={href}
                  href={href}
                  className="block rounded-md px-2 py-1 text-sm font-medium text-foreground transition-colors hover:bg-secondary/40"
                >
                  {label}
                </Link>
              ))}
              <Link
                href={defaultCtaHref}
                className="block rounded-md px-2 py-2 text-sm font-semibold text-foreground transition-colors hover:bg-secondary/40"
                data-marketing-auth-mobile-cta
                data-login-class="block rounded-md px-2 py-2 text-sm font-semibold text-foreground transition-colors hover:bg-secondary/40"
                data-auth-class="block rounded-md px-2 py-2 text-sm font-semibold text-foreground transition-colors hover:bg-secondary/40"
                data-auth-href={TIMELINE_ROUTE}
                data-auth-label="Open Timeline"
                data-login-label={defaultCtaLabel}
              >
                {defaultCtaLabel}
              </Link>
            </div>
          </details>
          <StaticPageLayoutAuthHydrator />
        </div>
      </div>
    </header>
  );
}

function MarketingFooterShell() {
  const currentYear = new Date().getFullYear();
  return (
    <footer className="mt-auto border-t border-border/60 bg-background/90 backdrop-blur">
      <div className="container flex w-full flex-col gap-4 py-4 text-xs text-muted-foreground/80 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-col items-center gap-3 sm:flex-row sm:items-center sm:gap-4">
          <Link href={ROOT_ROUTE} className="flex items-center" aria-label="Sweetistics home">
            <ThemeAwareLogo
              alt="Sweetistics"
              lightSrc="/assets/logo-text.png"
              darkSrc="/assets/logo-text-white.png"
              width={160}
              height={32}
              className="h-6 w-auto"
            />
          </Link>
          <div className="flex items-center gap-1.5">
            <span>©</span>
            <span>{currentYear}</span>
            <span>Amantus Machina GmbH</span>
          </div>
        </div>
        <div className="flex flex-col items-center gap-3 sm:flex-row sm:items-center sm:gap-6">
          <nav className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2 sm:h-10 sm:justify-end">
            {FOOTER_LINKS.map((link) =>
              link.external ? (
                <a
                  key={link.href}
                  href={link.href}
                  className="hover:text-foreground"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {link.label}
                </a>
              ) : (
                <Link key={link.href} href={link.href} className="hover:text-foreground">
                  {link.label}
                </Link>
              )
            )}
          </nav>
          <ThemeSlider className="shrink-0" />
        </div>
      </div>
    </footer>
  );
}

function MobileMenuIcon() {
  return (
    <svg
      aria-hidden="true"
      className="size-5 text-muted-foreground"
      fill="none"
      role="presentation"
      stroke="currentColor"
      strokeWidth="1.5"
      viewBox="0 0 24 24"
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
    </svg>
  );
}
